import { Zap } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { ZapDialog } from '@/components/ZapDialog';
import { cn } from '@/lib/utils';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useWallet } from '@/hooks/useWallet';
import { useZaps } from '@/hooks/useZaps';

interface ZapButtonProps {
  event: NostrEvent;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * A Lightning "zap" button for wiki contributions (NIP-57), styled to
 * match `StarButton`. Opens `ZapDialog` (amount presets, custom amount,
 * comment, and the NWC/WebLN/QR payment flow) and shows the running
 * total of sats zapped to this specific event.
 *
 * Renders nothing if the viewer isn't logged in, is the event's own
 * author (you can't zap yourself), or the author has no Lightning
 * address (`lud16`/`lud06`) configured on their profile — there would
 * be nowhere to send the payment.
 */
export function ZapButton({ event, size = 'sm', className }: ZapButtonProps) {
  const { user } = useCurrentUser();
  const author = useAuthor(event.pubkey);
  const { webln, activeNWC } = useWallet();
  const { totalSats, isLoading } = useZaps(event, webln, activeNWC);

  const hasLightningAddress = Boolean(author.data?.metadata?.lud16 || author.data?.metadata?.lud06);

  if (!user || user.pubkey === event.pubkey || !hasLightningAddress) {
    return null;
  }

  return (
    <ZapDialog target={event}>
      <Button
        type="button"
        variant="outline"
        size={size}
        title="Send a zap"
        className={cn('gap-1.5', className)}
      >
        <Zap className="size-4" />
        {isLoading ? '' : totalSats > 0 ? totalSats.toLocaleString() : 'Zap'}
      </Button>
    </ZapDialog>
  );
}
